/**
 * Browser push (Firebase Cloud Messaging) for members and volunteers.
 *
 * Gets this browser's current FCM token and stores it on the signed-in person's profile so campaigns
 * can reach them. FCM can rotate or invalidate a browser's token, so the token is re-read and re-saved on
 * every sign-in (silently, when permission was already granted) rather than saved once and trusted.
 *
 * Do NOT call deleteToken() or unsubscribe() here: doing that on page loads unregisters the token on
 * FCM's servers and caused persistent "registration-token-not-registered" failures in BespokeBeauty.
 */
import app from '../utils/firebase';
import { savePushToken } from './pushTokenApi';

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

const hasBrowserSupport = () =>
  typeof window !== 'undefined' &&
  'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

let supportedPromise = null;
const isSupported = () => {
  if (!hasBrowserSupport()) return Promise.resolve(false);
  if (!supportedPromise) {
    supportedPromise = import('firebase/messaging')
      .then(({ isSupported: check }) => check())
      .catch(() => false);
  }
  return supportedPromise;
};

export const isNotificationSupported = hasBrowserSupport;

export const getNotificationPermission = () =>
  ('Notification' in window ? Notification.permission : 'not-supported');

// A service worker cannot read this app's environment variables, so this environment's Firebase settings
// go in the worker's address. Dev, staging and production then each push through their own project.
const registerWorker = async () => {
  const params = new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  });
  const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
  if (!registration.active) {
    await Promise.race([
      new Promise((resolve) => {
        const poll = setInterval(() => { if (registration.active) { clearInterval(poll); resolve(); } }, 100);
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Service worker activation timed out')), 10000)),
    ]);
  }
  return registration;
};

/**
 * Returns this browser's current FCM token, or null.
 * @param {{prompt?: boolean}} opts  prompt=false never shows the browser's "Allow notifications?" dialog;
 *   it only succeeds when permission was already granted. Use prompt=true from a click / form submit.
 */
export const getPushToken = async ({ prompt = false } = {}) => {
  try {
    if (!(await isSupported())) return null;
    if (!VAPID_KEY) { console.log('Push: REACT_APP_VAPID_KEY is not set.'); return null; }

    let permission = Notification.permission;
    if (permission === 'default' && prompt) permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const { getMessaging, getToken } = await import('firebase/messaging');
    const registration = await registerWorker();
    const token = await getToken(getMessaging(app), { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    return token || null;
  } catch (err) {
    console.log('Push: could not get a token:', err.message);
    return null;
  }
};

/**
 * Get the browser's latest token and save it on the profile. Never throws.
 * @param {'member'|'volunteer'} type
 * @returns {Promise<'saved'|'no-token'|'save-failed'>}
 */
export const syncPushToken = async (type, opts) => {
  const token = await getPushToken(opts);
  if (!token) return 'no-token';
  try {
    await savePushToken(type, token);
    return 'saved';
  } catch (err) {
    console.log('Push: token was not saved:', err.message);
    return 'save-failed';
  }
};

/** Show pushes that arrive while the portal is open (the browser does not show them itself). */
export const onForegroundMessage = (callback) => {
  let unsubscribe = () => {};
  (async () => {
    try {
      if (!(await isSupported())) return;
      const { getMessaging, onMessage } = await import('firebase/messaging');
      unsubscribe = onMessage(getMessaging(app), (payload) => {
        const n = payload.notification;
        if (n && Notification.permission === 'granted') {
          navigator.serviceWorker.ready.then((reg) => reg.showNotification(n.title || 'New Notification', {
            body: n.body || '',
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            image: n.image || undefined,
            data: { ...(payload.data || {}), url: payload.fcmOptions?.link || payload.data?.url },
          }));
        }
        if (callback) callback(payload);
      });
    } catch (err) {
      console.log('Push: foreground listener not set up:', err.message);
    }
  })();
  return () => unsubscribe();
};
